import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { styled, media, Card, Layout, Link, Topbar } from '@pergas-common/pergas-components'
import { Add, Email, Globe, Phone } from '@pergas-common/pergas-icons'
import Divider from '../../Divider'
import { selectLocale, selectTenantIntegration } from '../../../redux/locale/selectors'
import { AddTagV2 } from '../../dialogs/AddTag'
import { api } from '../../../redux'
import useRequest from '../../../hooks/useRequest'
import { InputGroup, Select, Input, Textarea, itemsWithEmail } from '../../Input'
import { TagList } from '../../Tags'
import { PersonRoleGrid } from '../../PersonRoleGrid'
import { ProjectGrid } from '../../ProjectGrid'
import { ContactRoleGrid } from '../../ContactRoleGrid'
import { urlAsHttps } from '../../../util'
import { AddPersonRoleV2 } from '../../dialogs/AddPersonRole'
import { AddContactRoleV2 } from '../../dialogs/AddContactRole'
import { ContactPersonGridHoc } from '../../ContactPersonGrid'
import { useContactsRequest, useProjectRequest } from '../../../redux/contact/hooks'
import useDebounce from '../../../hooks/useDebounce'
import useUrlQuery from '../../../hooks/useUrlQuery'
import { makeSelectorEidById } from '../../../redux/contact/selectors'
import { makeSelectorPermissionContactPersons, makeSelectorPermissionProject } from '../../../redux/permission/selectors'

const RESPONSIBLE = 'responsible'
const stopPropagation = (e) => e.stopPropagation()

const FormHolder = styled.div`
  height: 100%;
`
const Holder = styled.div`
  height: 100%;
  overflow-y: scroll;
  background: rgb(247, 249, 250);
`
const Overflow = styled.div`
  overflow-y: hidden;
  height: 218px;
  padding-bottom: 10px;
`
const TagHolder = styled.div`
  width: 100%;
  > div {
    padding: 0px 6px 0 0;
  }
  ${media.medium`
    width: 92%;
  `}
  height: 268px;
`

const TopbarHolder = styled.div`
  overflow: hidden;
  height: 50px;
  background: ${({ theme }) => theme.palette.brand.secondary};
`
const Name = styled.span`
  ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.palette.common.primary};
`

const REQUEST_PARAMS = {
  limit: 50,
  sort: 'name.asc'
}

const OBJECT_TYPE_REQUEST_PARAMS = {
  query: [{ key: 'object_type_type', op: '=', value: 'contact' }],
  ...REQUEST_PARAMS
}

export default ({ disable, values, setFieldValue, handleChange, handleSubmit, children, original }) => {
  const query = useUrlQuery()
  const id = query.get('id')

  const tenantHasIntegration = useSelector(selectTenantIntegration)
  const locale = useSelector(selectLocale)
  const navigate = useNavigate()
  const selectContactPersonsPermissions = useMemo(makeSelectorPermissionContactPersons, [])
  const selectProjectPermissions = useMemo(makeSelectorPermissionProject, [])
  const contactPersonsPermissions = useSelector(selectContactPersonsPermissions)
  const projectsPermissions = useSelector(selectProjectPermissions)

  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false)
  const [isCreatePersonRoleOpen, setIsCreatePersonRoleOpen] = useState(false)
  const [isCreateContactRoleOpen, setIsCreateContactRoleOpen] = useState(false)
  const [getObjectTypes, objectTypes, objectTypesStatus] = useRequest(api.getObjectTypes, 'objectType')
  const [debouncedObjectTypesRequest] = useDebounce(getObjectTypes, 300)
  const [getRoles, roles] = useRequest(api.getRoles, 'roles')

  const [getEmployees, employees, employeeStatus] = useRequest(api.getEmployees, 'employee')
  const [debouncedEmployeesRequest] = useDebounce(getEmployees, 300)
  const selectorContactEidById = useMemo(makeSelectorEidById, [])

  const eId = useSelector((state) => selectorContactEidById(state, id))

  const isFetchingObjectTypes = objectTypesStatus === 'pending'
  const isFetchingEmployees = employeeStatus === 'pending'

  useEffect(() => {
    getObjectTypes(OBJECT_TYPE_REQUEST_PARAMS)
  }, [getObjectTypes])

  useEffect(() => {
    getEmployees(REQUEST_PARAMS)
  }, [getEmployees])

  useEffect(() => {
    getRoles(REQUEST_PARAMS)
  }, [getRoles])

  const responsibleRole = values.person_role?.find((pr) => pr.role_internal_name === RESPONSIBLE)
  const defaultResponsible = responsibleRole && { name: responsibleRole.person_name, id: responsibleRole.person_id }
  const defaultObjectType = values.object_type_name && values.object_type_id && { name: values.object_type_name, id: values.object_type_id }
  const homepageUri = urlAsHttps(values.homepage)

  const onTagSelect = useCallback((selected) => {
    const tag = { ...selected, tag_id: selected.id }
    if (values.tags == null) {
      setFieldValue('tags', [tag])
      return
    }
    const tagAlreadyExist = values.tags.some(({ tag_id: tagId }) => tagId === selected.id)
    if (!tagAlreadyExist) {
      setFieldValue('tags', [tag, ...values.tags])
    }
  }, [setFieldValue, values.tags])

  const onResponsible = useCallback((_, __, selected) => {
    const roleExists = roles?.find((r) => r.internal_name === RESPONSIBLE)
    const withoutPersonRole = values.person_role?.filter((pr) => pr.role_internal_name !== RESPONSIBLE) ?? []

    if (roleExists) {
      const mergedRoles = [...withoutPersonRole, {
        person_id: selected.id,
        person_name: selected.name,
        role_id: roleExists.id,
        role_name: roleExists.name,
        role_internal_name: roleExists.internal_name
      }]
      setFieldValue('person_role', mergedRoles)
    }
  }, [setFieldValue, roles, values.person_role])
  return (
    <FormHolder>
      <TopbarHolder>
        <Topbar left={<Name>{values.name}</Name>} />
      </TopbarHolder>
      <Holder>
        <Layout.Main>
          <Layout.Aside>
            <form onSubmit={handleSubmit} id='submitContact'>
              {isTagDialogOpen && <AddTagV2
                tagType='contact'
                isOpen={isTagDialogOpen}
                onCloseRequest={() => {
                  setIsTagDialogOpen(false)
                }}
                onSelect={onTagSelect}
                                  />}
              <InputGroup>
                <Input name='x_id' label={locale.id} disabled value={id ?? undefined} handleChange={() => {}} />
                <Input name='eid' label={locale.external_id} disabled={tenantHasIntegration} value={values.eid || eId} handleChange={handleChange} />
              </InputGroup>
              <Divider />
              <Input name='name' label={locale.contact} disabled={disable} value={values.name} handleChange={handleChange} />
              <Textarea name='description' label={locale.description} disabled={disable} value={values.description} handleChange={handleChange} />
              <Input name='vat_no' label={locale.vat_no} disabled={disable} value={values.vat_no} handleChange={handleChange} />
              <Divider />
              <Input name='address' label={locale.address} disabled={disable} value={values.address} handleChange={handleChange} />
              <InputGroup>
                <Input name='postal_code' label={locale.postal_code} disabled={disable} value={values.postal_code} handleChange={handleChange} />
                <Input name='city' label={locale.city} disabled={disable} value={values.city} handleChange={handleChange} />
              </InputGroup>
              <Divider />
              <Input name='phone' disabled={disable} value={values.phone} handleChange={handleChange}>
                <Link onClickHandler={stopPropagation} href={`tel:${values.phone}`}><Phone width={18} height={18} color='#28afe0' /></Link>
              </Input>
              <Input name='email' disabled={disable} value={values.email} handleChange={handleChange}>
                <Link onClickHandler={stopPropagation} href={`mailto:${values.email}`}><Email width={16} height={16} color='#28afe0' /></Link>
              </Input>
              <Input name='homepage' disabled={disable} value={values.homepage} handleChange={handleChange}>
                <Link onClickHandler={stopPropagation} href={homepageUri}><Globe width={18} height={18} color='#28afe0' /></Link>
              </Input>
              <Divider />
              <Select
                name='responsible_person_role'
                label={locale.contact_responsible}
                disabled={disable}
                defaultValue={defaultResponsible}
                request={(input) => {
                  debouncedEmployeesRequest({ query: [{ key: 'name', op: '~', value: input }], sort: 'name.asc', limit: 50 })
                }}
                requestStatus={isFetchingEmployees}
                items={itemsWithEmail(employees)}
                handleChange={onResponsible}
              />
              <Select
                name='object_type_id'
                label={locale.contact_object_type}
                disabled={disable}
                defaultValue={defaultObjectType}
                request={(input) => {
                  debouncedObjectTypesRequest({
                    query: [
                      { key: 'object_type_type', op: '=', value: 'contact' },
                      { key: 'name', op: '~', value: input }
                    ],
                    limit: 50
                  })
                }}
                requestStatus={isFetchingObjectTypes}
                items={objectTypes}
                handleChange={setFieldValue}
              />
              <TagHolder>
                <Card.Content>
                  <Card.Header title={locale.tags}>
                    {!disable && (
                      <Link onClickHandler={() => { !disable && setIsTagDialogOpen(true) }}>
                        <Add color='#28afe0' width={20} height={20} />
                      </Link>
                    )}
                  </Card.Header>
                  <Overflow>
                    <TagList name='tags' disabled={disable} tags={values.tags} handleChange={setFieldValue} />
                  </Overflow>
                </Card.Content>
              </TagHolder>
            </form>
          </Layout.Aside>
          <Layout.Section>
            {children}
            <Card.Content>
              <Card.Header title={locale.contact_persons}>
                {contactPersonsPermissions.canCreate && (
                  <Link onClickHandler={() => { navigate('/contact-persons/new', { state: { contact: { ...original } } }) }}>
                    <Add color='#28afe0' width={20} height={20} />
                  </Link>)}
              </Card.Header>
              <Overflow><ContactPersonGridHoc columns='2fr 2fr 1fr 1fr' request={useContactsRequest} collectionId={id} /></Overflow>
            </Card.Content>
            <Card.Content>
              <Card.Header title={locale.projects}>
                {projectsPermissions.canCreate && (
                  <Link onClickHandler={() => { navigate('/projects/new', { state: { contact: { ...original } } }) }}>
                    <Add color='#28afe0' width={20} height={20} />
                  </Link>)}
              </Card.Header>
              <Overflow><ProjectGrid request={useProjectRequest} collectionId={id} columns='4fr 2fr 3fr' /></Overflow>
            </Card.Content>
            <Card.Content>
              <Card.Header title={locale.external_contacts}>
                {!disable && (
                  <Link onClickHandler={() => { setIsCreateContactRoleOpen(true) }}>
                    <Add color='#28afe0' width={20} height={20} />
                  </Link>
                )}
              </Card.Header>
              <Overflow>
                <ContactRoleGrid canEdit={!disable}>
                  {isCreateContactRoleOpen && (
                    <AddContactRoleV2
                      isOpen={isCreateContactRoleOpen} onCloseRequest={() => {
                        setIsCreateContactRoleOpen(false)
                      }}
                    />
                  )}
                </ContactRoleGrid>
              </Overflow>
            </Card.Content>
            <Card.Content>
              <Card.Header title={locale.external_contact_persons}>
                {!disable && (
                  <Link onClickHandler={() => { setIsCreatePersonRoleOpen(true) }}>
                    <Add color='#28afe0' width={20} height={20} />
                  </Link>
                )}
              </Card.Header>
              <Overflow>
                <PersonRoleGrid
                  values={values.person_role} onChangeHandler={(v) => {
                    setFieldValue('person_role', v)
                  }} canEdit={!disable}
                >
                  {isCreatePersonRoleOpen && (
                    <AddPersonRoleV2
                      values={values.person_role}
                      onChangeHandler={(v) => {
                        setFieldValue('person_role', v)
                      }}
                      isOpen={isCreatePersonRoleOpen}
                      onCloseRequest={() => {
                        setIsCreatePersonRoleOpen(false)
                      }}
                    />
                  )}
                </PersonRoleGrid>
              </Overflow>
            </Card.Content>
          </Layout.Section>
        </Layout.Main>
      </Holder>
    </FormHolder>
  )
}
